import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert } from "../Alerts/actions";
import {
  getNextOfKinSuccess,
  getNextOfKinFailure,
  saveNextOfKinSuccess,
  saveNextOfKinFailure,
  fetchOtherContactsSuccess,
  fetchOtherContactsFailure,
  addOtherContactSuccess,
  addOtherContactFailure,
  editOtherContactSuccess,
  editOtherContactFailure,
  deleteOtherContactSuccess,
  deleteOtherContactFailure,
  searchContactsSuccess,
  searchContactsFailure,
  getContactSuggestionsSuccess,
  getContactSuggestionsFailure,
} from "./actions";
import {
  GET_NEXT_OF_KIN,
  SAVE_NEXT_OF_KIN,
  FETCH_OTHER_CONTACTS,
  ADD_OTHER_CONTACT,
  EDIT_OTHER_CONTACT,
  DELETE_OTHER_CONTACT,
  SEARCH_CONTACTS,
  GET_CONTACT_SUGGESTIONS,
} from "./constants";

export function* fetchNextOfKinSaga({ payload, onGet }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(api(token).get, `/api/next-of-kin/${payload}`);
    const formattedData = {
      primaryContact:
        data?.find((item) => item?.contact_type === "primary") ?? {},
      secondaryContact:
        data?.find((item) => item?.contact_type === "secondary") ?? {},
    };
    yield put(getNextOfKinSuccess(formattedData));
    typeof onGet === "function" && onGet(formattedData);
  } catch (e) {
    yield put(getNextOfKinFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching residence!"));
  }
}

export function* saveNextOfKinSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/update-next-of-kin/${residentId}`,
      formData,
    );
    yield put(saveNextOfKinSuccess(data));
  } catch (e) {
    yield put(saveNextOfKinFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching residence!"));
  }
}

/** ResourceCollection + LengthAwarePaginator can nest rows at data.data.data. */
const normalizeOtherContactsPage = (data) => {
  if (Array.isArray(data)) {
    return {
      data,
      total: data.length,
      current_page: 1,
      last_page: 1,
    };
  }
  const rows = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.data?.data)
      ? data.data.data
      : [];
  return {
    ...data,
    data: rows,
    current_page: data?.current_page ?? 1,
    last_page: data?.last_page ?? 1,
    total: data?.total ?? rows.length,
  };
};

export function* fetchOtherContactsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload?.data ? QueryString.stringify(payload.data) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident-contact?${query}`,
    );
    yield put(fetchOtherContactsSuccess(normalizeOtherContactsPage(data)));
  } catch (e) {
    yield put(fetchOtherContactsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching other contacts!"));
  }
}

export function* addOtherContactSaga({ payload, onAdd, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-contact/add`,
      payload,
    );
    yield put(addOtherContactSuccess(data));
    typeof onAdd === "function" && onAdd(data);
  } catch (e) {
    yield put(addOtherContactFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while adding other contact!"));
  }
}

export function* editOtherContactSaga({ payload, onEdit, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-contact/edit/${id}`,
      formData,
    );
    yield put(editOtherContactSuccess(data));
    typeof onEdit === "function" && onEdit(data);
  } catch (e) {
    yield put(editOtherContactFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while editing other contact!"));
  }
}

export function* deleteOtherContactSaga({ payload, onDelete, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).delete,
      `/api/resident-contact/${payload}`,
    );
    yield put(deleteOtherContactSuccess(data));
    typeof onDelete === "function" && onDelete(data);
  } catch (e) {
    yield put(deleteOtherContactFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while deleting other contact!"));
  }
}

export function* searchContactsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident-contact-search?${query}`,
    );
    yield put(searchContactsSuccess(data));
  } catch (e) {
    yield put(searchContactsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while searching contacts!"));
  }
}

export function* getContactSuggestionsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/governance-item?${query}`,
    );
    yield put(getContactSuggestionsSuccess(data));
  } catch (e) {
    yield put(getContactSuggestionsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while getting contact suggestions!"));
  }
}

export function* fetchNextOfKinFlow() {
  yield takeEvery(GET_NEXT_OF_KIN, fetchNextOfKinSaga);
}

export function* saveNextOfKinFlow() {
  yield takeEvery(SAVE_NEXT_OF_KIN, saveNextOfKinSaga);
}
export function* fetchOtherContactsFlow() {
  yield takeEvery(FETCH_OTHER_CONTACTS, fetchOtherContactsSaga);
}
export function* addOtherContactFlow() {
  yield takeEvery(ADD_OTHER_CONTACT, addOtherContactSaga);
}
export function* editOtherContactFlow() {
  yield takeEvery(EDIT_OTHER_CONTACT, editOtherContactSaga);
}
export function* deleteOtherContactFlow() {
  yield takeEvery(DELETE_OTHER_CONTACT, deleteOtherContactSaga);
}
export function* searchContactsFlow() {
  yield takeEvery(SEARCH_CONTACTS, searchContactsSaga);
}
export function* getContactSuggestionsFlow() {
  yield takeEvery(GET_CONTACT_SUGGESTIONS, getContactSuggestionsSaga);
}

export default function* ResidentNetworkSaga() {
  yield all([
    fetchNextOfKinFlow(),
    saveNextOfKinFlow(),
    fetchOtherContactsFlow(),
    addOtherContactFlow(),
    editOtherContactFlow(),
    deleteOtherContactFlow(),
    searchContactsFlow(),
    getContactSuggestionsFlow(),
  ]);
}
